import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";

import parse, { HTMLReactParserOptions } from "html-react-parser";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

export interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        overriddenTitle: string;
        tabUniqueId: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
}

interface CustomTabsStyles {
    wrapper?: InjectableCss;
    tabs?: InjectableCss;
    tabButton?: ButtonPresentationProps;
    tabContent?: InjectableCss;
}

export const CustomTabsStyles: CustomTabsStyles = {
    tabs: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            gap: 6px;
            background-color: #dfe7ed;
            border-radius: 30px;
            padding: 6px;
            width: fit-content;
            flex-wrap: wrap;
            .tab-button-wrapper {
                position: relative;
                z-index: 1;
                flex-shrink: 0;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            overflow-x: auto;
                            flex-wrap: wrap;
                            -webkit-overflow-scrolling: touch;
                            scrollbar-width: none;
                            &::-webkit-scrollbar {
                                display: none;
                            }
                            padding: 10px !important;
                            gap: 3px !important;
                        `,
                        css`
                            width: 100%;
                            overflow-x: auto;
                            flex-wrap: wrap;
                            -webkit-overflow-scrolling: touch;
                            scrollbar-width: none;
                            &::-webkit-scrollbar {
                                display: none;
                            }
                            padding: 10px !important;
                            gap: 3px !important;
                        `,
                        null,
                        null,
                        null,
                    ],
                );
            }}
        `,
    },
    tabButton: {
        css: css`
            border: none;
            border-radius: 30px;
            cursor: pointer;
            * {
                color: #636161;
                line-height: normal;
            }
            line-height: 20px;
            text-align: center;
            background-color: transparent;
            padding: 10px 24px;
            min-width: unset;
            white-space: nowrap;
            outline: none;
            border-color: transparent;
            span {
                font-size: 12px;
                font-weight: 400;
            }

            &:focus {
                outline: none;
            }
            &:hover {
                background-color: transparent;
            }
            &.active {
                background-color: #1e6e9b;
                * {
                    color: #fff;
                }
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 7px 10px;
                            height: auto;
                            min-height: auto;
                            border: 1px solid #1e6e9b !important;
                            span {
                                color: #1e6e9b;
                            }
                        `,
                        css`
                            padding: 7px 10px;
                            height: auto;
                            min-height: auto;
                            border: 1px solid #1e6e9b !important;
                            span {
                                color: #1e6e9b;
                            }
                        `,
                        null,
                        null,
                        null,
                    ]
                );
            }}
        `,
    },
    tabContent: {
        css: css`
            margin-top: 40px;
            margin-bottom: 40px;
        `,
    },
};

const styles = CustomTabsStyles;

type Props = OwnProps;

const CustomTabs: React.FC<Props> = ({ id, fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const [activeTab, setActiveTab] = useState(0);

    const handleTabClick = (index: number) => {
        setActiveTab(index);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper className="tabs" {...styles.tabs}>
                {links.links.map((link, index) => (
                    <div
                        key={index}
                        className="tab-button-wrapper"
                    >
                        <Button
                            {...styles.tabButton}
                            className={`tab-button ${activeTab === index ? "active" : ""}`}
                            onClick={() => handleTabClick(index)}
                        >
                            {parse(fields.links[index].fields.overriddenTitle, parserOptions)}
                        </Button>
                    </div>
                ))}
            </StyledWrapper>
            <StyledWrapper className="tab-content" {...styles.tabContent}>
                <Zone
                    contentId={id}
                    zoneName={fields.links[activeTab]?.fields.tabUniqueId || fields.links[activeTab]?.fields.overriddenTitle}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: CustomTabs,
    definition: {
        group: "Basic",
        displayName: "Custom Tabs",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: item => {
                    const {
                        destination: { value },
                        overriddenTitle,
                    } = item.fields;
                    return parse(overriddenTitle, parserOptions) || value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" },
                        isVisible: () => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "tabUniqueId",
                        displayName: "Unique ID",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
